import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApartmentViewContext } from '../../../context/ApartmentViewContext';
import { Context } from '../../../../../Context';
import CompartmentTile from './CompartmentTile';
import styles from '../styles/compartmentsGrid.module.scss';

/**
 * ArrangeMode Grid View - Grid-based compartments display for arrangement selection
 * Similar to InfoMode but:
 * - No expansion on click
 * - Shows arrangement version (e.g., "2/3")
 * - Cycles through arrangements on click
 * 
 * @param {Array} indoorCompartments - Indoor compartments data
 * @param {Array} outdoorCompartments - Outdoor compartments data
 * @param {Object} interactions - Interaction handlers
 * @param {number} hoveredCompartmentInd - Currently hovered compartment index
 * @param {Function} getCompartmentName - Function to get compartment display name
 * @param {Function} handleRoomClickInArrangeMode - Click handler for arrangement cycling
 * @param {Array} arrangementVersions - Array of available versions per compartment
 * @param {Array} userSelectedArrangements - Current selected arrangement per compartment
 * @param {number} urlComboParam - Current encoded combination number
 * @param {number} numberOfPossibleArrangements - Total possible arrangement combinations
 * @param {number} [columnsPerRow=3] - Number of columns in grid (default: 3)
 */
function ArrangeModeGridView({
	indoorCompartments,
	outdoorCompartments,
	interactions,
	hoveredCompartmentInd,
	getCompartmentName,
	handleRoomClickInArrangeMode,
	arrangementVersions,
	userSelectedArrangements,
	urlComboParam,
	numberOfPossibleArrangements,
	columnsPerRow = 3,
}) {
	const { t } = useTranslation();
	const { activeApartmentSelectedTier, setActiveApartmentSelectedTier } = useContext(Context);
	const { handleRoomHover } = interactions;

	// Format number with Polish locale
	const formatNumber = (number) => {
		if (isNaN(number)) return number;
		return number.toLocaleString('pl-PL', { style: 'decimal' });
	};

	/**
	 * Group compartments into rows based on columnsPerRow
	 */
	const groupIntoRows = useMemo(() => {
		return (compartments) => {
			const rows = [];
			for (let i = 0; i < compartments.length; i += columnsPerRow) {
				rows.push(compartments.slice(i, i + columnsPerRow));
			}
			return rows;
		};
	}, [columnsPerRow]);

	const onClickCompartment = (compartment, hasMultipleArrangements) => {
		if (hasMultipleArrangements) {
			handleRoomClickInArrangeMode(compartment.number);
		}
		if (activeApartmentSelectedTier !== compartment.ref_tier) {
			setActiveApartmentSelectedTier(compartment.ref_tier);
		}
	};

	/**
	 * Render a single compartment tile for ArrangeMode
	 */
	const renderTile = (compartment, isOutdoor = false) => {
		const isHovered = hoveredCompartmentInd === compartment.number;
		const compartmentArrangements = arrangementVersions[compartment.number - 1];
		const hasMultipleArrangements = compartmentArrangements > 1;

		return (
			<CompartmentTile
				key={`${isOutdoor ? 'outdoor' : 'indoor'}_compartment_${compartment.number}`}
				compartment={compartment}
				isSelected={false} // Never selected in ArrangeMode
				isHovered={isHovered}
				onClick={
					hasMultipleArrangements
						// ? () => handleRoomClickInArrangeMode(compartment.number)
						? () => onClickCompartment(compartment, hasMultipleArrangements)
						: undefined
				}
				onHover={() => handleRoomHover(compartment.number, false)}
				onHoverLeave={() => handleRoomHover(null)}
				labelText={getCompartmentName(compartment)}
				valueContent={
					hasMultipleArrangements ? (
						<>
							<span>
								{userSelectedArrangements[compartment.number - 1] + 1}
							</span>
							<span style={{ fontSize: '0.85em', opacity: 0.7 }}>
								{` / ${compartmentArrangements}`}
							</span>
						</>
					) : (
						'1'
					)
				}
				expandedContent={null} // No expansion in ArrangeMode
				hasTooltip={false}
				tooltipContent={null}
				style={
					!hasMultipleArrangements
						? { cursor: 'auto', opacity: 0.6 }
						: {}
				}
			/>
		);
	};

	/**
	 * Render rows of compartment tiles (no expansion)
	 */
	const renderCompartmentRows = (compartments, isOutdoor = false) => {
		const rows = groupIntoRows(compartments);

		return rows.map((row, rowIndex) => (
			<div key={`${isOutdoor ? 'outdoor' : 'indoor'}_row_${rowIndex}`}>
				<div className={styles.apartment_info_data_container}>
					{row.map((compartment) => renderTile(compartment, isOutdoor))}

					{/* Fill empty slots if row is not complete */}
					{row.length < columnsPerRow &&
						Array.from({ length: columnsPerRow - row.length }).map((_, i) => (
							<div
								key={`empty_${rowIndex}_${i}`}
								style={{ minHeight: '1px' }}
							/>
						))}
				</div>
			</div>
		));
	};

	return (
		<div className={styles.compartments_grid_container}>
		<div>
				<span style={{ 
					// fontSize: '0.85rem',
					display: 'block' 
					}}>
					{t('arrange_mode_description_1')}
				</span>
				<span style={{ 
					// fontSize: '0.85rem',
					display: 'block' 
					}}>
					{t('arrange_mode_description_2')}<br/>
					{t('arrange_mode_description_3')}
				</span>
			</div>
			{/* Indoor Compartments Section */}
			{indoorCompartments.length > 0 && (
				<div className={styles.compartments_section}>
					{renderCompartmentRows(indoorCompartments, false)}
				</div>
			)}

			<hr />

			{/* Outdoor Compartments Section */}
			{outdoorCompartments.length > 0 && (
				<div className={styles.compartments_section}>
					{renderCompartmentRows(outdoorCompartments, true)}
				</div>
			)}

			<div>
				<span style={{ fontSize: '0.85rem', color: '#666', display: 'block' }}>
					{t('arrange_mode_disclaimer_1')}<br/>
					{t('arrange_mode_disclaimer_2')}
				</span>
				{/* Show current arrangement selection info */}
				{numberOfPossibleArrangements > 1 ? (
					<p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
						{t('chosen_version')} <b>{formatNumber(urlComboParam + 1)}</b> {t('out_of')}
						{' '}
						{formatNumber(numberOfPossibleArrangements)} {t('possible')}.
					</p>
				) : (
					<p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
						{t('no_versions_available')}
					</p>
				)}
			</div>
		</div>
	);
}

export default ArrangeModeGridView;
